import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowRight, ExternalLink, Shield, Factory, Check,
  ChevronDown, BookOpen,
} from 'lucide-react';

const COMPETITION_TRACKS = [
  {
    id: 'fll',
    title: 'FLL Challenge',
    subtitle: 'FIRST LEGO League',
    grades: 'Grades 3–8',
    accentColor: '#ef5b5b',
    interestClass: 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100',
    image: 'https://res.cloudinary.com/dfa8raimq/image/upload/v1787588558/_28A4256_bgclpt.jpg',
    description: 'Teams design, build, and program LEGO robots to complete missions while also developing an innovation project and presenting their work.',
    tags: ['LEGO SPIKE', 'Missions', 'Innovation Project', 'Teamwork'],
    interestHref: 'https://docs.google.com/forms/d/1-rQ6jHOTm0oPXdsGI9j-Z1k9ihx27uRuDMV5UcBqMBA/edit?ts=6aadaa93',
    learnHref: 'https://www.firstillinoisrobotics.org/',
  },
  {
    id: 'ftc',
    title: 'FTC',
    subtitle: 'FIRST Tech Challenge',
    grades: 'Grades 6–12',
    accentColor: '#ff8a2a',
    interestClass: 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100',
    image: 'https://res.cloudinary.com/dfa8raimq/image/upload/v1787589487/_28A4289.jpg_Users_diegoguzmanfavela_Pictures_Editing_Workspace_APS_Automate_2026_Photos_Final__28A4289_1_zdrkon.jpg',
    description: 'Students design, build, and program reusable competition robots for alliance-based matches, engineering challenges, and judged awards.',
    tags: ['Robot Design', 'Programming', 'Engineering Notebook', 'Competition'],
    interestHref: 'https://docs.google.com/forms/d/1-rQ6jHOTm0oPXdsGI9j-Z1k9ihx27uRuDMV5UcBqMBA/edit?ts=6aadaa93',
    learnHref: 'https://www.firstillinoisrobotics.org/',
  },
  {
    id: 'vex',
    title: 'VEX IQ',
    subtitle: 'VEX Robotics',
    grades: 'Grades 3–8',
    accentColor: '#1dbf91',
    interestClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100',
    image: 'https://res.cloudinary.com/dfa8raimq/image/upload/v1790794686/VexIQ_mudgov.jpg',
    description: 'Teams build and program VEX IQ robots for collaborative game challenges that develop mechanical design, coding, strategy, and teamwork.',
    tags: ['VEX IQ', 'Robot Skills', 'Teamwork Challenge', 'VEXcode'],
    interestHref: 'https://docs.google.com/forms/d/1-rQ6jHOTm0oPXdsGI9j-Z1k9ihx27uRuDMV5UcBqMBA/edit?ts=6aadaa93',
    learnHref: 'https://gainingsteam.net/help/about-vex-iq/what-is-vex-iq',
  },
];

const FANUC_CERTS = [
  { cert: 'Handling Tool Operation', target: 'High school juniors / seniors', status: 'Available Now', live: true },
  { cert: 'iRVision 2D/3D',          target: 'Post-secondary',               status: 'Coming 2027',   live: false },
  { cert: 'Multi-Process (Weld/Paint)', target: 'Post-secondary',            status: 'Coming 2027',   live: false },
  { cert: 'ROBOGUIDE Simulation',    target: 'Advanced',                     status: 'Coming 2028',   live: false },
];

const SCHOLARSHIP_POINTS = [
  'Merit-based scholarships ranging from $1,000 to full tuition',
  'Dedicated FIRST participant application pools',
  'Opportunities across engineering, business, and liberal arts programs',
  'Recognition that students can include on college applications and resumes',
];

export default function RoboticsHubPage() {
  return (
    <div data-testid="robotics-hub-page">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847] text-white">
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
            maskImage: 'linear-gradient(to bottom, black, transparent 88%)',
          }}
        />
        <div className="absolute top-0 right-[20%] w-96 h-96 bg-blue-400/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-[6%] w-72 h-72 bg-rsc-yellow/10 rounded-full blur-[90px] pointer-events-none" />

        <div className="container-app relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 items-center py-20 lg:py-24">

            {/* Left: copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/40 bg-rsc-yellow/8 text-rsc-yellow text-xs font-bold uppercase tracking-widest mb-5" data-testid="hero-badge">
                Robotics Programs, Teams &amp; Training
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.98] tracking-tight mb-5">
                Build.<br />Compete.<br /><span className="text-rsc-yellow">Advance.</span>
              </h1>
              <p className="text-lg text-white/65 max-w-xl mb-8 leading-relaxed">
                A focused robotics hub for students who want to build real skills, join competitive teams,
                and explore the path from youth robotics to industrial automation.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#competitions">
                  <Button
                    className="bg-rsc-yellow text-rsc-navy font-bold rounded-[14px] px-7 h-12 text-base shadow-lg shadow-rsc-yellow/20 hover:shadow-rsc-yellow/30 hover:scale-[1.02] transition-all"
                    data-testid="hero-explore-btn"
                  >
                    Explore Competition Teams <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#spotlight">
                  <Button
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 rounded-[14px] h-12 px-7 text-base"
                    data-testid="hero-team-btn"
                  >
                    Meet the Team
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: robot image (desktop only) */}
            <div className="relative hidden lg:block" style={{ minHeight: '420px' }}>
              <div className="absolute inset-6 left-8 rounded-[28px] overflow-hidden border border-white/15 shadow-[0_30px_100px_rgba(0,0,0,.32)] bg-[#0b1e39]">
                <img
                  src="https://res.cloudinary.com/dfa8raimq/image/upload/v1790794178/FTC_Robot_Cyberbyte_fidpxl.jpg"
                  alt="Cyberbyte FTC robot"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rsc-navy/55 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 z-10">
                  <p className="text-white font-bold text-base leading-tight">Competitive robotics in action</p>
                  <p className="text-white/55 text-xs mt-0.5">Real builds. Real teams. Real competition.</p>
                </div>
              </div>
              {/* floating chips */}
              <div className="absolute left-0 bottom-20 z-20 bg-white text-rsc-navy rounded-2xl px-3 py-2.5 shadow-xl text-sm font-bold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-rsc-yellow inline-block" />FLL Challenge
              </div>
              <div className="absolute right-0 top-14 z-20 bg-white text-rsc-navy rounded-2xl px-3 py-2.5 shadow-xl text-sm font-bold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />FTC
              </div>
              <div className="absolute right-6 bottom-8 z-20 bg-white text-rsc-navy rounded-2xl px-3 py-2.5 shadow-xl text-sm font-bold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />VEX IQ
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/20" />
        </div>
      </section>

      {/* ═══════════ COMPETITION TRACKS ═══════════ */}
      <section id="competitions" className="py-16 bg-gray-50" data-testid="competition-tracks-section">
        <div className="container-app">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-navy/20 bg-rsc-navy/5 text-rsc-navy text-xs font-bold uppercase tracking-widest mb-3">
              Competitive Robotics
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-rsc-navy mb-3 tracking-tight">
              Choose a competition path
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto">
              Only the three competitive robotics programs Ready Set STEM currently supports are shown.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMPETITION_TRACKS.map((track) => (
              <div
                key={track.id}
                data-testid={`track-${track.id}`}
                className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md flex flex-col"
              >
                {/* accent bar */}
                <div className="h-1.5 w-full" style={{ background: track.accentColor }} />

                {/* image */}
                <div className="aspect-[16/10] overflow-hidden relative bg-gray-100">
                  <img
                    src={track.image}
                    alt={track.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.025]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
                  <span className="absolute left-3 top-3 z-10 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-[11px] font-bold text-rsc-navy shadow-sm">
                    {track.subtitle}
                  </span>
                </div>

                {/* body */}
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-2xl font-extrabold text-rsc-navy tracking-tight">{track.title}</h3>
                    <span className="shrink-0 text-xs font-bold text-gray-500 bg-gray-100 border border-gray-200 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {track.grades}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{track.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {track.tags.map((t) => (
                      <span key={t} className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200">{t}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2.5 mt-auto pt-1">
                    <a href={track.interestHref} target="_blank" rel="noopener noreferrer" className="block">
                      <button
                        className={`w-full h-10 text-sm font-bold rounded-xl transition-colors ${track.interestClass}`}
                        data-testid={`${track.id}-interest-btn`}
                      >
                        I'm Interested
                      </button>
                    </a>
                    <a href={track.learnHref} target="_blank" rel="noopener noreferrer" className="block">
                      <button
                        className="w-full h-10 text-sm font-bold rounded-xl border border-gray-300 bg-white text-rsc-navy hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                        data-testid={`${track.id}-learn-btn`}
                      >
                        Learn More <ExternalLink className="w-3 h-3" />
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ TEAM SPOTLIGHT ═══════════ */}
      <section id="spotlight" className="py-16 bg-gradient-to-b from-[#eef3fa] to-[#f7f9fc]" data-testid="team-spotlight-section">
        <div className="container-app">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-navy/20 bg-rsc-navy/5 text-rsc-navy text-xs font-bold uppercase tracking-widest mb-3">
              Team Spotlight
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-rsc-navy mb-3 tracking-tight">
              Follow the team beyond competition day
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto">
              Current team stories, social channels, and official competition results.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6">
            {/* Blog placeholder */}
            <div className="bg-white rounded-[24px] border border-gray-200 shadow-xl overflow-hidden flex flex-col" data-testid="blog-spotlight-card">
              <div className="aspect-[16/8] bg-gradient-to-br from-rsc-navy/10 to-blue-400/15 flex items-center justify-center border-b border-gray-200">
                <div className="text-center text-gray-400 px-6">
                  <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm font-semibold">Latest robotics blog post featured image</p>
                </div>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <p className="text-xs font-bold uppercase tracking-widest text-violet-600 mb-2">Latest Robotics Blog</p>
                <h3 className="text-2xl font-extrabold text-rsc-navy leading-tight mb-3 tracking-tight">
                  Latest robotics story loads here automatically
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  This card will show the newest published post tagged "Robotics," including the title,
                  publish date, short excerpt, featured image, and destination URL.
                </p>
                <Link to="/blog" className="mt-auto">
                  <Button variant="outline" className="border-gray-300 rounded-xl h-10 text-sm font-bold">
                    Read Latest Post <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Cyberbyte FTC team card */}
            <div className="bg-white rounded-[24px] border border-gray-200 shadow-xl p-7 flex flex-col gap-5" data-testid="cyberbyte-team-card">
              {/* team header */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-[18px] border border-gray-200 shadow-md overflow-hidden shrink-0 bg-white flex items-center justify-center">
                  <img
                    src="https://res.cloudinary.com/dfa8raimq/image/upload/v1790794756/E0F7B3CB-4D04-4933-92EC-31980BC7C3B1_frehts.png"
                    alt="Cyberbyte FTC 23408 logo"
                    className="w-full h-full object-contain p-1.5"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-rsc-navy tracking-tight">Cyberbyte FTC 23408</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Team social channels and official event record</p>
                </div>
              </div>

              {/* social links */}
              <div className="flex flex-col gap-2.5">
                <a
                  href="https://www.facebook.com/FTC23408Cyberbyte/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 no-underline border border-gray-200 bg-gray-50 rounded-2xl px-4 py-3.5 font-bold text-rsc-navy hover:border-gray-300 hover:bg-white hover:translate-x-1 transition-all"
                  data-testid="ftc-facebook-link"
                >
                  <span>
                    Facebook
                    <small className="block text-sm font-semibold text-gray-400 mt-0.5">FTC23408Cyberbyte</small>
                  </span>
                  <ExternalLink className="w-4 h-4 text-gray-400 shrink-0" />
                </a>
                <a
                  href="https://www.instagram.com/cyberbyteofficial/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 no-underline border border-gray-200 bg-gray-50 rounded-2xl px-4 py-3.5 font-bold text-rsc-navy hover:border-gray-300 hover:bg-white hover:translate-x-1 transition-all"
                  data-testid="ftc-instagram-link"
                >
                  <span>
                    Instagram
                    <small className="block text-sm font-semibold text-gray-400 mt-0.5">@cyberbyteofficial</small>
                  </span>
                  <ExternalLink className="w-4 h-4 text-gray-400 shrink-0" />
                </a>
              </div>

              {/* competition record */}
              <a
                href="https://ftc-events.firstinspires.org/2025/team/23408"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline block p-5 rounded-2xl bg-gradient-to-br from-rsc-navy to-[#284e80] text-white relative overflow-hidden"
                data-testid="ftc-record-link"
              >
                <div className="absolute -right-8 -top-10 w-36 h-36 rounded-full bg-rsc-yellow/15 pointer-events-none" />
                <p className="font-bold text-base relative z-10">View 2025 Performance Record →</p>
                <p className="text-white/60 text-sm mt-1.5 relative z-10 leading-relaxed">
                  Open the team's official FIRST event page for results, awards, and competition history.
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ SCHOLARSHIPS ═══════════ */}
      <section className="py-16 bg-white" data-testid="first-scholarships-section">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            <div className="p-7 rounded-[22px] bg-gradient-to-br from-[#fff9e8] to-[#fffdf7] border border-[#f0d48c] shadow-md">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-[#fff3c5] border border-[#f4d97b] flex items-center justify-center text-2xl shrink-0 mt-0.5">
                  🏆
                </div>
                <div>
                  <h2 className="text-2xl font-extrabold text-rsc-navy mb-2 tracking-tight">
                    $80M+ in FIRST Scholarships Annually
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    FRC and FTC participants gain access to scholarship opportunities from 200+ colleges and universities across the United States.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SCHOLARSHIP_POINTS.map((point) => (
                      <div key={point} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-600 leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FANUC ADVANTAGE ═══════════ */}
      <section className="relative py-16 bg-gray-50" data-testid="advantage-section">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-200 bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                <Shield className="w-3.5 h-3.5" /> Industrial Robotics Access
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-rsc-navy mb-3 leading-tight tracking-tight">
                7 FANUC Industrial Robots.<br />
                <span className="text-orange-600">Training Beyond Competition.</span>
              </h2>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Students can move from competitive robotics into hands-on industrial automation experience.
                Ready Set STEM's robotics ecosystem connects youth programs with FANUC training opportunities
                and a direct path toward{' '}
                <span className="text-rsc-navy font-semibold">FANUC Authorized Satellite Training (FAST)</span> certification.
              </p>
              <div className="space-y-2.5">
                {FANUC_CERTS.map((c) => (
                  <div key={c.cert} className="flex items-center justify-between gap-4 p-3 rounded-xl bg-white border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2.5">
                      <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-rsc-navy">{c.cert}</p>
                        <p className="text-[11px] text-gray-400">{c.target}</p>
                      </div>
                    </div>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${c.live ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                      {c.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative rounded-[22px] overflow-hidden aspect-[4/3] shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=700&auto=format"
                  alt="FANUC Industrial Robotics Lab"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-rsc-navy via-rsc-navy/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-bold text-sm">FANUC Industrial Robotics Lab</p>
                  <p className="text-white/50 text-xs mt-0.5">6 stations — pick-and-place, palletizing, vision, advanced applications</p>
                </div>
              </div>
              <a href="https://www.apstrainingacademy.org/adult-courses/" target="_blank" rel="noopener noreferrer" className="block">
                <div className="p-4 rounded-2xl bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Factory className="w-5 h-5 text-blue-600 shrink-0" />
                      <div>
                        <h3 className="text-sm font-bold text-rsc-navy">Adult Industrial Robotics Training</h3>
                        <p className="text-xs text-gray-500">FANUC certified courses via APS Training Academy</p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-blue-600 shrink-0" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
